// The bell's notices (Ruben, 2 Nov 2026: « build the bell as you proposed »):
// the live « À traiter » alerts of ops_overview (0043) turned into notices,
// grouped by urgency, each counted as new until this computer has seen it, and
// each remembered for a while once its problem ends. Step 1 of the proposal:
// seen state per computer; per person comes with the signals table (SW-18).
import { S } from './strings';
import type { Alert } from './types';

export type Level = 'now' | 'today';
export type Act = 'assign' | 'order' | 'home' | 'remind';

export interface Notice {
  /** One per notice; `keys` are what is counted as seen (one per home in a grouped notice). */
  id: string; keys: string[]; level: Level; title: string; sub: string; at: string | null;
  act: Act; target: string[];
}
export interface Resolved { id: string; title: string; sub: string; at: number }

export function noticesFrom(alerts: Alert[]): Notice[] {
  return alerts.map((a): Notice => {
    switch (a.kind) {
      case 'unassigned': {
        const o = a.order;
        return { id: 'u:' + o.id, keys: ['u:' + o.id], level: 'now', title: S.alert.unassigned, sub: `${o.ref} · ${o.customer.name}, ${o.customer.quartier}`, at: o.created_at, act: 'assign', target: [o.id] };
      }
      case 'failed_install': {
        const o = a.order;
        return { id: 'f:' + o.id, keys: ['f:' + o.id], level: 'now', title: S.alert.failed, sub: `${o.ref} · ${o.customer.name}, ${o.customer.quartier}`, at: o.events.failed_first_install ?? o.created_at, act: 'order', target: [o.id] };
      }
      case 'silent_gateway': {
        const h = a.home;
        const hrs = h.received_at ? Math.max(36, Math.round((Date.now() - new Date(h.received_at).getTime()) / 3_600_000)) : 36;
        return { id: 's:' + h.customer_id, keys: ['s:' + h.customer_id], level: 'today', title: S.alert.silent(hrs), sub: `${h.gateway} · ${h.name}, ${h.quartier}`, at: h.received_at, act: 'home', target: [h.customer_id] };
      }
      case 'low_without_order':
        return {
          id: 'low', keys: a.homes.map((h) => 'l:' + h.customer_id), level: 'today', title: S.bell.low(a.homes.length),
          sub: a.homes.map((h) => `${h.name} (${h.level} %)`).join(', '), at: null, act: 'remind', target: a.homes.map((h) => h.customer_id),
        };
    }
  }).sort((x, y) => (x.level === y.level ? (y.at ?? '').localeCompare(x.at ?? '') : x.level === 'now' ? -1 : 1));
}

/** A notice is new while any of its keys has not been seen on this computer. */
export const isNew = (n: Notice, seen: Record<string, number>) => n.keys.some((k) => !(k in seen));

/** Keys that appeared since the previous list (the first list of a visit announces nothing). */
export function arrived(prev: Set<string> | null, ns: Notice[]): Notice[] {
  if (!prev) return [];
  return ns.filter((n) => n.keys.some((k) => !prev.has(k)));
}

/** Notices whose problem ended since the previous list. */
export function gone(prev: Notice[], ns: Notice[], now = Date.now()): Resolved[] {
  const still = new Set(ns.flatMap((n) => n.keys));
  return prev.filter((p) => p.keys.every((k) => !still.has(k))).map((p) => ({ id: p.id, title: p.title, sub: p.sub, at: now }));
}

/** Seen keys are kept a week, so a notice that comes back after that counts as new again. */
export function pruneSeen(seen: Record<string, number>, now = Date.now()): Record<string, number> {
  return Object.fromEntries(Object.entries(seen).filter(([, t]) => now - t < 7 * 86_400_000));
}

/** The last few resolved notices of the past 12 hours, newest first. */
export const keepResolved = (rs: Resolved[], now = Date.now()) =>
  rs.filter((r) => now - r.at < 12 * 3_600_000).sort((a, b) => b.at - a.at).slice(0, 5);
